import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { MapPin, Clock, Calendar, IndianRupee, BookOpen, Layers, ArrowLeft, Send, Lock } from "lucide-react";
import { DashboardShell } from "../../components/shared/DashboardShell";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { studentNav } from "../../components/shared/navConfig";
import { tasks as mockTasks } from "../../data/mockData";
import { useAuth } from "../../context/AppContext";
import api from "../../lib/api";
import { ApplyTaskDialog } from "../../components/shared/ApplyTaskDialog";
import { toast } from "sonner";

export default function TaskDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [liveTask, setLiveTask] = useState(null);
  const [profile, setProfile] = useState(null);
  const [applyOpen, setApplyOpen] = useState(false);
  const kycApproved = user?.kyc_status === "approved";

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/tasks/detail/${id}`); setLiveTask(data); }
      catch {}
      try { const { data } = await api.get("/profile/me"); setProfile(data.profile); }
      catch {}
    })();
  }, [id]);

  // Normalize live task to same shape as mock task
  const task = liveTask ? {
    id: liveTask.task_id,
    task_id: liveTask.task_id,
    title: liveTask.title,
    company: liveTask.company_name,
    company_name: liveTask.company_name,
    category: liveTask.category,
    location: liveTask.address || liveTask.city || "—",
    coordinates: "—",
    distance: liveTask.work_mode === "Remote" ? "Remote" : "—",
    skills: liveTask.skills || [],
    education: liveTask.education || "—",
    experience: liveTask.experience || "—",
    budget: liveTask.budget || 0,
    deadline: liveTask.deadline || "—",
    startDate: liveTask.start_date || "—",
    startTime: liveTask.start_time || "—",
    workingHours: liveTask.working_hours || "—",
    workMode: liveTask.work_mode || "On-site",
    status: liveTask.status || "Open",
    posted: "recently",
    description: liveTask.description || "",
  } : (mockTasks.find(t => t.id === id) || mockTasks[0]);

  const apply = () => {
    if (!liveTask) {
      // Fallback for mock tasks (no backend record)
      toast.success("Demo application submitted");
      setTimeout(() => navigate("/student/applications"), 700);
      return;
    }
    setApplyOpen(true);
  };

  return (
    <DashboardShell nav={studentNav} title={task.title} subtitle={`${task.company} · ${task.id}`}>
      <Link to="/student/tasks" data-testid="back-to-tasks-link" className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-white mb-5 transition-colors duration-200"><ArrowLeft size={12}/> Back to tasks</Link>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="card-surface p-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="eyebrow">{task.category}</div>
                <h2 className="font-display text-2xl text-white mt-1">{task.title}</h2>
                <div className="text-sm text-zinc-500 mt-1">{task.company}</div>
              </div>
              <StatusBadge status={task.status}/>
            </div>
            <div className="divider-h my-6"/>
            <p className="text-sm text-zinc-300 leading-relaxed">{task.description}</p>

            <div className="grid sm:grid-cols-2 gap-4 mt-6">
              <Info icon={MapPin}     l="Location"       v={task.location}/>
              <Info icon={Layers}     l="Work mode"      v={task.workMode}/>
              <Info icon={IndianRupee}l="Budget"         v={`₹${task.budget.toLocaleString()}`}/>
              <Info icon={Clock}      l="Working hours"  v={task.workingHours}/>
              <Info icon={Calendar}   l="Start"          v={`${task.startDate} · ${task.startTime}`}/>
              <Info icon={Calendar}   l="Deadline"       v={task.deadline}/>
              <Info icon={BookOpen}   l="Education"      v={task.education}/>
              <Info icon={Layers}     l="Experience"     v={task.experience}/>
            </div>
          </div>

          <div className="card-surface p-6">
            <div className="eyebrow mb-3">Required skills</div>
            <div className="flex flex-wrap gap-2">
              {task.skills.map(s => (
                <span key={s} className="text-xs px-3 py-1 rounded-full border" style={{ borderColor: "var(--border-strong)", background: "var(--surface-2)", color: "#E4E4E7" }}>{s}</span>
              ))}
            </div>
          </div>
        </div>

        {/* Apply column */}
        <div className="space-y-6">
          <div className="card-surface p-6 relative overflow-hidden">
            <div className="absolute inset-0 grid-bg-fine opacity-60"/>
            <div className="relative">
              <div className="eyebrow">Compensation</div>
              <div className="font-display text-4xl text-white mt-2 tabular-nums flex items-baseline">
                <IndianRupee size={22} className="text-zinc-500"/>{task.budget.toLocaleString()}
              </div>
              <div className="text-xs text-zinc-500 mt-1">Escrowed · released on approval</div>
              {kycApproved ? (
                <button data-testid="apply-task-btn" onClick={apply} className="btn-primary w-full py-3 rounded-md mt-5 text-sm font-medium inline-flex items-center justify-center gap-2">
                  <Send size={14}/> Apply for task
                </button>
              ) : (
                <div className="mt-5">
                  <button disabled data-testid="apply-locked-btn" className="w-full py-3 rounded-md text-sm font-medium inline-flex items-center justify-center gap-2 border cursor-not-allowed"
                          style={{ borderColor: "rgba(245,158,11,0.35)", background: "rgba(245,158,11,0.08)", color: "#F59E0B" }}>
                    <Lock size={14}/> KYC approval required
                  </button>
                  <Link to="/student/onboarding" className="text-[11px] text-blue-400 hover:text-blue-300 block mt-2 text-center">Complete or update your KYC →</Link>
                </div>
              )}
              <div className="text-[11px] text-zinc-500 mt-3 text-center">Your KYC-verified profile will be shared with {task.company}</div>
            </div>
          </div>

          {/* Map placeholder */}
          <div className="card-surface p-4">
            <div className="eyebrow mb-2">Map</div>
            <div className="relative rounded-md overflow-hidden aspect-[4/3] border" style={{ borderColor: "var(--border)", background: "linear-gradient(135deg,#0f172a,#0b1220)" }}>
              <div className="absolute inset-0 grid-bg-fine opacity-70"/>
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                <div className="w-10 h-10 rounded-full flex items-center justify-center pulse-dot"
                     style={{ background: "rgba(59,130,246,0.2)", border: "2px solid #3B82F6" }}>
                  <MapPin size={14} className="text-blue-300"/>
                </div>
              </div>
              <div className="absolute bottom-3 left-3 right-3 glass rounded-md p-2 text-[11px] text-zinc-300 font-mono">
                {task.coordinates} · {task.distance} away
              </div>
            </div>
          </div>
        </div>
      </div>

      <ApplyTaskDialog
        open={applyOpen}
        onOpenChange={setApplyOpen}
        task={task}
        profile={profile}
        onDone={() => setTimeout(() => navigate("/student/applications"), 500)}
      />
    </DashboardShell>
  );
}

const Info = ({ icon: Icon, l, v }) => (
  <div className="flex items-start gap-3">
    <div className="w-8 h-8 rounded-md flex items-center justify-center shrink-0" style={{ background: "var(--surface-2)", border: "1px solid #27272A" }}>
      <Icon size={13} className="text-blue-400"/>
    </div>
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{l}</div>
      <div className="text-sm text-zinc-200 mt-0.5">{v}</div>
    </div>
  </div>
);
